(() => {
  if (customElements.get('ils-collection-loader')) return;
  class CollectionLoader extends HTMLElement {
    connectedCallback() {
      if (this.observer || !('IntersectionObserver' in window) || !this.dataset.next) return;
      this.root = this.closest('[data-section-type="collection-grid"]');
      this.grid = this.root?.querySelector('.collection-grid__wrapper > .grid');
      if (!this.grid) return;
      this.setAttribute('data-auto', '');
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.load();
      }, {rootMargin: '0px 0px 1000px 0px'});
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.controller?.abort();
    }
    async load() {
      if (this.loading || !this.dataset.next || !this.isConnected) return;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      this.controller = new AbortController();
      const next = new URL(this.dataset.next, location.origin);
      const pageUrl = next.href;
      next.searchParams.set('section_id', this.dataset.sectionId);
      const timeout = setTimeout(() => this.controller.abort(), 15000);
      try {
        const response = await fetch(next, {signal: this.controller.signal});
        if (!response.ok) throw new Error('Collection page unavailable');
        const html = await response.text();
        if (!this.isConnected) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('.collection-grid__wrapper > .grid');
        if (!source) throw new Error('Collection grid missing');
        const existing = new Set([...this.grid.children].map(card => card.dataset.productId));
        const cards = [...source.children].filter(card => card.dataset.productId && !existing.has(card.dataset.productId));
        if (!cards.length) throw new Error('Collection page empty');
        const fragment = document.createDocumentFragment();
        for (const card of cards) {
          card.dataset.collectionPage = pageUrl;
          card.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; img.decoding = 'async'; });
          card.querySelectorAll('script:not([type="application/json"])').forEach(script => script.remove());
          fragment.append(card);
        }
        this.grid.append(fragment);
        this.bindSwatches(cards);
        window.AOS?.refreshHard?.();
        if (window.theme?.settings?.quickView) {
          for (const card of cards) {
            const preload = () => {
              if (window.theme.config.bpSmall) return;
              card.removeEventListener('mouseover', preload);
              card.removeEventListener('focusin', preload);
              window.theme.preloadProductModal(card.dataset.productHandle, card.dataset.productId, card.querySelector('.quick-product__btn'));
            };
            card.addEventListener('mouseover', preload);
            card.addEventListener('focusin', preload);
          }
        }
        const following = doc.querySelector('ils-collection-loader');
        const fallback = following?.querySelector('[data-pagination-fallback]');
        if (fallback) this.querySelector('[data-pagination-fallback]').replaceChildren(...fallback.childNodes);
        this.dataset.next = following?.dataset.next || '';
        document.dispatchEvent(new CustomEvent('collection:appended', {detail: {items: cards, url: pageUrl}}));
        this.observer?.unobserve(this);
        if (this.dataset.next) this.observer?.observe(this);
        else this.hidden = true;
      } catch (error) {
        if (!this.isConnected) return;
        this.observer?.disconnect();
        this.removeAttribute('data-auto');
      } finally {
        clearTimeout(timeout);
        this.loading = false;
        this.removeAttribute('aria-busy');
      }
    }
    bindSwatches(cards) {
      for (const card of cards) {
        card.querySelectorAll('.color-swatch--with-image').forEach(swatch => {
          const image = card.querySelector('.grid-product__color-image--' + swatch.dataset.variantId);
          if (!image) return;
          swatch.addEventListener('mouseenter', () => {
            image.style.backgroundImage = 'url(' + swatch.dataset.variantImage + ')';
            image.classList.add('is-active');
          });
          swatch.addEventListener('mouseleave', () => image.classList.remove('is-active'));
        });
      }
    }
  }
  customElements.define('ils-collection-loader', CollectionLoader);
})();
